import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import {Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import dailyListService from "../../service/DailyListService";
import {parseAndFormatDateForInput} from "../../util/DateUtil";

const ImportFromPoolDialog = ({open, onClose, onImport}) => {

    const {t} = useTranslation();

    const [date, setDate] = useState(parseAndFormatDateForInput(new Date()));
    const [dailyLists, setDailyLists] = useState([]);
    const [selectedDailyList, setSelectedDailyList] = useState(null);
    const [selectedMatches, setSelectedMatches] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedDailyList(null);
            setSelectedMatches([]);
            searchDailyLists(date);
        }
    }, [open]);

    const searchDailyLists = async (searchDate) => {
        try {
            setLoading(true);
            const result = await dailyListService.search({
                page: 0,
                data: {date: searchDate},
                max: 50
            });
            setDailyLists(result?.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const onDateChange = (e) => {
        const newDate = e.target.value;
        setDate(newDate);
        setSelectedDailyList(null);
        setSelectedMatches([]);
        searchDailyLists(newDate);
    };

    const onSelectDailyList = (dl) => {
        setSelectedDailyList(dl);
        setSelectedMatches([]);
    };

    const onToggleMatch = (match) => {
        setSelectedMatches(prev => {
            const exists = prev.find(m => m._id === match._id && m.index === match.index);
            if (exists) {
                return prev.filter(m => !(m._id === match._id && m.index === match.index));
            }
            return [...prev, match];
        });
    };

    const onToggleAll = () => {
        if (!selectedDailyList?.matches) return;
        if (selectedMatches.length === selectedDailyList.matches.length) {
            setSelectedMatches([]);
        } else {
            setSelectedMatches([...selectedDailyList.matches]);
        }
    };

    const isMatchSelected = (match) => {
        return selectedMatches.some(m => m._id === match._id && m.index === match.index);
    };

    const handleImport = () => {
        onImport(selectedMatches);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>{t('title.importFromPool')}</DialogTitle>
            <DialogContent>
                <TextField
                    type="date"
                    label={t('label.date')}
                    value={date}
                    onChange={onDateChange}
                    InputLabelProps={{shrink: true}}
                    size="small"
                    sx={{mt: 1, mb: 2}}
                    fullWidth
                />

                {!selectedDailyList && (
                    <>
                        <Typography variant="subtitle2" sx={{mb: 1}}>
                            {t('title.selectDailyList')}
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('label.title')}</TableCell>
                                        <TableCell>{t('label.application')}</TableCell>
                                        <TableCell>{t('label.date')}</TableCell>
                                        <TableCell/>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dailyLists.map((dl) => (
                                        <TableRow key={dl._id} hover sx={{cursor: 'pointer'}}
                                                  onClick={() => onSelectDailyList(dl)}>
                                            <TableCell>{dl.title}</TableCell>
                                            <TableCell>{dl.application?.name}</TableCell>
                                            <TableCell>{parseAndFormatDateForInput(dl.date)}</TableCell>
                                            <TableCell>
                                                <Button size="small" onClick={() => onSelectDailyList(dl)}>
                                                    {t('button.view')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {dailyLists.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                {t('message.noResultFound')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {selectedDailyList && (
                    <>
                        <Button size="small" sx={{mb: 1}}
                                onClick={() => {
                                    setSelectedDailyList(null);
                                    setSelectedMatches([]);
                                }}>
                            {t('button.back')}
                        </Button>
                        <Typography variant="subtitle2" sx={{mb: 1}}>
                            {selectedDailyList.title}
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedDailyList.matches?.length > 0 &&
                                                    selectedMatches.length === selectedDailyList.matches.length}
                                                indeterminate={selectedMatches.length > 0 &&
                                                    selectedMatches.length < selectedDailyList.matches?.length}
                                                onChange={onToggleAll}
                                            />
                                        </TableCell>
                                        <TableCell>{t('label.index')}</TableCell>
                                        <TableCell>{t('label.league')}</TableCell>
                                        <TableCell>{t('label.teamHome')}</TableCell>
                                        <TableCell>{t('label.teamAway')}</TableCell>
                                        <TableCell>{t('label.time')}</TableCell>
                                        <TableCell>{t('label.forecast')}</TableCell>
                                        <TableCell>{t('label.odds')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedDailyList.matches?.map((match, idx) => (
                                        <TableRow key={idx} hover sx={{cursor: 'pointer'}}
                                                  onClick={() => onToggleMatch(match)}>
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isMatchSelected(match)}/>
                                            </TableCell>
                                            <TableCell>{match.index}</TableCell>
                                            <TableCell>{match.league?.name}</TableCell>
                                            <TableCell>{match.teamHome?.name}</TableCell>
                                            <TableCell>{match.teamAway?.name}</TableCell>
                                            <TableCell>{match.time}</TableCell>
                                            <TableCell>{match.forecast}</TableCell>
                                            <TableCell>{match.odds}</TableCell>
                                        </TableRow>
                                    ))}
                                    {(!selectedDailyList.matches || selectedDailyList.matches.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                {t('title.noMatchesFound')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('button.cancel')}</Button>
                <Button
                    variant="contained"
                    disabled={selectedMatches.length === 0}
                    onClick={handleImport}>
                    {t('button.import')} ({selectedMatches.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportFromPoolDialog;
